import { Fragment, useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import DialogWrapper from './Wrapper';
import useApp from '../../../hooks/useApp';
import { getErrorMessage } from '../../../lib/error';
import { PaymentMethod } from '../../../lib/interface';
import { getSDKClient } from '../../../lib/sdk';

type PaymentMethodToCancel = {
  payerName: string;
  paymentMethodId: string;
  paymentMethodData: PaymentMethod;
  customerAppId: string;
  status: string;
};

const getPaymentMethodDetails = (pm: PaymentMethod): string => {
  if (pm.payment_method_type === 'MANDATE') {
    const account = pm.mandate.sender_account;
    const accountDetails = [account?.institution_name, account?.account_number_masked].filter(Boolean);
    const identifiers = accountDetails.length > 0 ? accountDetails : [pm.mandate.mandate_details.upi_id];
    return [pm.payment_method_type, ...identifiers].filter(Boolean).join(' ');
  }
  if (pm.payment_method_type === 'CARD') {
    const card = pm.card.card_details;
    const last4 = card?.last4 ? `*${card.last4}` : '';
    const expiry = card?.expiry_month && card?.expiry_year ? `(${card.expiry_month}/${card.expiry_year})` : '';
    return [pm.payment_method_type, card?.brand, last4, expiry].filter(Boolean).join(' ');
  }
  return '';
};

// CancelPaymentMethod is a dialog to confirm and cancel a stored payment method
export default function CancelPaymentMethod({
  open,
  handleClose,
  handleCloseWithRefresh,
  paymentMethod,
}: {
  open: boolean;
  handleClose: () => void;
  handleCloseWithRefresh: () => void;
  paymentMethod: PaymentMethodToCancel;
}) {
  const { app } = useApp();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  // Bumped on close/unmount so a late cancel (PM1) cannot refresh while another dialog (PM2) is open.
  const requestIdRef = useRef(0);
  // 3s success timer; cleared if the user closes before it fires.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  const resetStateAndClose = () => {
    requestIdRef.current += 1;
    clearTimeout(refreshTimerRef.current);
    setSubmitting(false);
    setSuccess(false);
    setErrMsg('');
    handleClose();
  };

  const cancelPaymentMethod = async () => {
    const requestId = requestIdRef.current;
    setSubmitting(true);
    setErrMsg('');
    const sdk = getSDKClient(app.env, app.tokens.get(app.env) ?? '');
    try {
      await sdk.cancelPaymentMethod(paymentMethod.customerAppId, paymentMethod.paymentMethodId);
      if (requestId !== requestIdRef.current) return; // dialog was closed; ignore this response
      setSuccess(true);
      refreshTimerRef.current = setTimeout(handleCloseWithRefresh, 3000); // show the message for 3 seconds
    } catch (err) {
      if (requestId !== requestIdRef.current) return; // dialog was closed; ignore this response
      setErrMsg(getErrorMessage(err));
      setSubmitting(false);
    }
  };

  const rows = [
    ['Payer:', paymentMethod.payerName],
    ['Payment Method ID:', paymentMethod.paymentMethodId],
    ['Payment Method Details:', getPaymentMethodDetails(paymentMethod.paymentMethodData)],
    ['Status:', success ? 'CANCELLED' : paymentMethod.status],
  ];

  return (
    <DialogWrapper
      id="cancel-payment-method"
      onClose={resetStateAndClose}
      aria-labelledby="cancel-payment-method"
      title="Cancel payment method"
      open={open}
    >
      <DialogContent dividers>
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">Payment method was cancelled successfully.</Alert>
            </Grid>
          )}
          {errMsg !== '' && (
            <Grid item xs={12}>
              <Alert severity="error">{errMsg}</Alert>
            </Grid>
          )}
          {rows.map(([label, value]) => (
            <Fragment key={label}>
              <Grid item xs={5}>
                {label}
              </Grid>
              <Grid item xs={7}>
                {value}
              </Grid>
            </Fragment>
          ))}
          <Grid container item xs={12} sx={{ justifyContent: 'center' }}>
            <Typography variant="body2" component="div">
              Once cancelled, you can no longer collect payments (including autopay) using this payment method.
            </Typography>
          </Grid>
          <Grid container item xs={12} sx={{ justifyContent: 'center' }}>
            <Button variant="contained" color="error" disabled={submitting || success} onClick={cancelPaymentMethod}>
              CANCEL PAYMENT METHOD
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </DialogWrapper>
  );
}
